<h1 mat-dialog-title class="dtd-title">
  <span>{{ nodeLabel(content) }}</span>
  <span class="dtd-title-structure" *ngIf="linkedStructureLabel">
    &mdash; {{ 'settings.documentTemplate.linkedStructure' | translate }}: {{ linkedStructureLabel }}
  </span>
  <span class="dt-badge dt-badge-deleted" *ngIf="content.deleted">
    {{ 'settings.documentTemplate.badges.hidden' | translate }}
  </span>
</h1>

<mat-dialog-content>

  <!-- BEGIN - Level 2: the section itself -->
  <div class="dtd-section">
    <div class="dt-identifiers">
      <div class="dt-identifier">
        <span class="dt-identifier-label">{{ 'settings.documentTemplate.fields.contentCode' | translate }}</span>
        <code>{{ content.code }}</code>
      </div>
      <button
        mat-stroked-button
        color="warn"
        class="dt-identifier-btn"
        *ngIf="!contentIdentifiersUnlocked"
        (click)="onUnlockContentIdentifiers()">
        <mat-icon fontSet="fal" fontIcon="fa-lock"></mat-icon>
        {{ 'settings.documentTemplate.actions.unlockIdentifier' | translate }}
      </button>
      <span class="dt-identifier-unlocked" *ngIf="contentIdentifiersUnlocked">
        <mat-icon fontSet="fal" fontIcon="fa-unlock"></mat-icon>
        {{ 'settings.documentTemplate.identifierUnlocked' | translate }}
      </span>
    </div>
    <div class="dt-hint dt-hint-warn">
      <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
      <span>{{ 'settings.documentTemplate.hints.identifierWarning' | translate }}</span>
    </div>

    <div class="dt-hint dt-hint-warn" *ngIf="contentCheckIsDegraded">
      <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
      <span>{{ 'settings.documentTemplate.hints.duplicateCheckUnavailable' | translate }}</span>
    </div>

    <div class="dt-form form-wrapper mt-2">
      <form [formGroup]="contentForm" (ngSubmit)="onContentSubmit()">
        <formly-form
          [form]="contentForm"
          [fields]="contentFields"
          [model]="contentModel"
          [options]="contentOptions">
        </formly-form>
      </form>
      <mat-spinner-button
        (btnClick)="onContentSubmit()"
        role="submit"
        [options]="contentBtnOptions"
        class="btn-elevate">
      </mat-spinner-button>
    </div>
  </div>
  <!-- END - Level 2: the section itself -->

  <hr />

  <!-- BEGIN - Level 3: documents -->
  <div class="dtd-docs-header">
    <span class="dtd-docs-title">
      {{ 'settings.documentTemplate.docsTitle' | translate }}
      <span class="dt-count" *ngIf="docs.length">{{ docs.length }}</span>
    </span>
    <mat-slide-toggle [(ngModel)]="includeDeleted" (change)="onIncludeDeletedChange()">
      {{ 'settings.documentTemplate.showHidden' | translate }}
    </mat-slide-toggle>
    <span class="dtd-spacer"></span>
    <span
      [matTooltip]="dataTypesErrorMessage
        ? dataTypesErrorMessage
        : ('settings.documentTemplate.hints.dataTypeCatalogEmpty' | translate)"
      [matTooltipDisabled]="dataTypesAreLoading || selectableDataTypes.length > 0">
      <button
        mat-raised-button
        color="primary"
        [disabled]="dataTypesAreLoading || !selectableDataTypes.length"
        (click)="onAddDocClicked()">
        <mat-icon fontSet="fal" fontIcon="fa-plus"></mat-icon>
        {{ 'settings.documentTemplate.actions.addDoc' | translate }}
      </button>
    </span>
  </div>

  <!-- The data type catalogue drives the whole level 3 editor: its state is explicit -->
  <div class="dt-hint dt-hint-danger" *ngIf="dataTypesErrorMessage">
    <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
    <span>{{ dataTypesErrorMessage }}</span>
    <button
      mat-stroked-button
      type="button"
      class="dtd-hint-btn"
      (click)="loadDataTypes()">
      <mat-icon fontSet="fal" fontIcon="fa-redo"></mat-icon>
      {{ 'settings.documentTemplate.actions.reloadDataTypes' | translate }}
    </button>
  </div>

  <div class="dt-hint dt-hint-warn" *ngIf="dataTypeCatalogIsEmpty">
    <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
    <span>{{ 'settings.documentTemplate.hints.dataTypeCatalogEmpty' | translate }}</span>
  </div>

  <div class="dtd-inline-spinner" *ngIf="docsPanelIsLoading">
    <mat-spinner strokeWidth="3" [diameter]="28" mode="indeterminate"></mat-spinner>
  </div>

  <div class="row" *ngIf="!docsPanelIsLoading">
    <div class="col-12 col-lg-5">
      <div class="dtd-empty" *ngIf="docs.length === 0">
        {{ 'settings.documentTemplate.empty.docsText' | translate }}
      </div>

      <div
        *ngIf="docs.length"
        class="dt-list"
        cdkDropList
        [cdkDropListData]="docs"
        (cdkDropListDropped)="onDocDropped($event)">
        <div
          class="dt-row"
          *ngFor="let doc of docs"
          cdkDrag
          [cdkDragDisabled]="reorderIsSaving"
          [class.dt-row-selected]="selectedDoc && selectedDoc._id === doc._id"
          [class.dt-row-hidden]="doc.deleted">

          <mat-icon
            class="dt-drag-handle"
            cdkDragHandle
            fontSet="fal"
            fontIcon="fa-grip-vertical"
            [matTooltip]="'settings.documentTemplate.hints.drag' | translate"></mat-icon>

          <button type="button" class="dt-row-main" (click)="onDocClicked(doc)">
            <span class="dt-code">{{ doc.key }}</span>
            <span class="dt-label">{{ nodeLabel(doc) }}</span>
            <span class="dt-badges">
              <span class="dt-badge dt-badge-deleted" *ngIf="doc.deleted">
                {{ 'settings.documentTemplate.badges.hidden' | translate }}
              </span>
              <span class="dt-badge dt-badge-neutral" *ngIf="doc.mandatory">
                {{ 'settings.documentTemplate.badges.mandatory' | translate }}
              </span>
              <span class="dt-badge dt-badge-structure" *ngIf="doc.contract">
                {{ 'settings.documentTemplate.badges.contract' | translate }}
              </span>
              <span class="dt-badge dt-badge-neutral" *ngIf="doc.privacy">
                {{ 'settings.documentTemplate.badges.privacy' | translate }}
              </span>
              <span
                class="dt-badge dt-badge-neutral"
                *ngIf="dataTypeLabelFor(doc.dataTypeId)"
                [matTooltip]="'settings.documentTemplate.fields.dataType' | translate">
                {{ dataTypeLabelFor(doc.dataTypeId) }}
              </span>
              <span
                class="dt-badge dt-badge-warn"
                *ngIf="dataTypeIsUnavailable(doc.dataTypeId)"
                [matTooltip]="'settings.documentTemplate.errors.dataTypeUnavailable' | translate">
                {{ 'settings.documentTemplate.badges.dataTypeUnavailable' | translate }}
              </span>
            </span>
          </button>

          <div class="dt-row-actions">
            <button
              mat-icon-button
              *ngIf="!doc.deleted"
              [disabled]="moveIsLoading"
              (click)="onMoveDoc(doc)"
              [matTooltip]="'settings.documentTemplate.actions.move' | translate">
              <mat-icon fontSet="fal" fontIcon="fa-exchange"></mat-icon>
            </button>
            <button
              mat-icon-button
              color="warn"
              *ngIf="!doc.deleted"
              (click)="onDeleteDoc(doc)"
              [matTooltip]="'settings.documentTemplate.actions.delete' | translate">
              <mat-icon fontSet="fal" fontIcon="fa-trash-alt"></mat-icon>
            </button>
            <button
              mat-icon-button
              color="primary"
              *ngIf="doc.deleted"
              [disabled]="restoreDocIsBlocked"
              (click)="onRestoreDoc(doc)"
              [matTooltip]="restoreDocIsBlocked
                ? ('settings.documentTemplate.hints.restoreBlockedDoc' | translate)
                : ('settings.documentTemplate.actions.restore' | translate)">
              <mat-icon fontSet="fal" fontIcon="fa-trash-restore"></mat-icon>
            </button>
          </div>
        </div> <!-- /.dt-row -->
      </div> <!-- /.dt-list -->

      <div class="dt-legend" *ngIf="includeDeleted">
        <mat-icon fontSet="fal" fontIcon="fa-info-circle"></mat-icon>
        <span>{{ 'settings.documentTemplate.hints.restoreNotCascading' | translate }}</span>
      </div>
    </div> <!-- /.col -->

    <div class="col-12 col-lg-7">
      <!-- Document editor -->
      <ng-container *ngIf="docPanel === 'doc' && selectedDoc">
        <div class="dtd-panel-title">
          <span>{{ nodeLabel(selectedDoc) }}</span>
          <button
            mat-icon-button
            (click)="docPanel = 'none'"
            [attr.aria-label]="'common.cta.close' | translate">
            <mat-icon fontSet="fal" fontIcon="fa-times"></mat-icon>
          </button>
        </div>

        <div class="dt-identifiers">
          <div class="dt-identifier">
            <span class="dt-identifier-label">{{ 'settings.documentTemplate.fields.key' | translate }}</span>
            <code>{{ selectedDoc.key }}</code>
          </div>
          <button
            mat-stroked-button
            color="warn"
            class="dt-identifier-btn"
            *ngIf="!docIdentifiersUnlocked"
            (click)="onUnlockDocIdentifiers()">
            <mat-icon fontSet="fal" fontIcon="fa-lock"></mat-icon>
            {{ 'settings.documentTemplate.actions.unlockIdentifier' | translate }}
          </button>
          <span class="dt-identifier-unlocked" *ngIf="docIdentifiersUnlocked">
            <mat-icon fontSet="fal" fontIcon="fa-unlock"></mat-icon>
            {{ 'settings.documentTemplate.identifierUnlocked' | translate }}
          </span>
        </div>

        <div class="dt-hint dt-hint-danger" *ngIf="dataTypeIsUnavailable(docModel.dataTypeId)">
          <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
          <span>{{ 'settings.documentTemplate.hints.dataTypeUnavailable' | translate }}</span>
        </div>

        <div class="dt-hint dt-hint-warn" *ngIf="!docModel.dataTypeId">
          <mat-icon fontSet="fal" fontIcon="fa-info-circle"></mat-icon>
          <span>{{ 'settings.documentTemplate.hints.dataTypeMissing' | translate }}</span>
        </div>

        <div class="dt-form form-wrapper mt-2">
          <form [formGroup]="docForm" (ngSubmit)="onDocSubmit()">
            <formly-form
              [form]="docForm"
              [fields]="docFields"
              [model]="docModel"
              [options]="docOptions">
            </formly-form>
          </form>

          <div class="dtd-quickpicks">
            <span class="dtd-quickpicks-label">
              {{ 'settings.documentTemplate.fields.expirationPeriod' | translate }}:
            </span>
            <button
              mat-stroked-button
              type="button"
              class="dtd-quickpick"
              *ngFor="let period of expirationPeriodSuggestions"
              (click)="onPickExpirationPeriod(period, false)">
              {{ period }}
            </button>
            <button
              mat-stroked-button
              type="button"
              class="dtd-quickpick"
              (click)="onPickExpirationPeriod('', false)">
              {{ 'settings.documentTemplate.noExpirationPeriod' | translate }}
            </button>
          </div>

          <mat-spinner-button
            (btnClick)="onDocSubmit()"
            role="submit"
            [options]="docBtnOptions"
            class="btn-elevate">
          </mat-spinner-button>
        </div>
      </ng-container>

      <!-- New document editor -->
      <ng-container *ngIf="docPanel === 'newDoc'">
        <div class="dtd-panel-title">
          <span>{{ 'settings.documentTemplate.newDocTitle' | translate }}</span>
          <button
            mat-icon-button
            (click)="docPanel = 'none'"
            [attr.aria-label]="'common.cta.close' | translate">
            <mat-icon fontSet="fal" fontIcon="fa-times"></mat-icon>
          </button>
        </div>

        <div class="dt-hint dt-hint-warn">
          <mat-icon fontSet="fal" fontIcon="fa-exclamation-triangle"></mat-icon>
          <span>{{ 'settings.documentTemplate.hints.keyWarning' | translate }}</span>
        </div>

        <div class="dt-form form-wrapper mt-2">
          <form [formGroup]="newDocForm" (ngSubmit)="onNewDocSubmit()">
            <formly-form
              [form]="newDocForm"
              [fields]="newDocFields"
              [model]="newDocModel"
              [options]="newDocOptions">
            </formly-form>
          </form>

          <div class="dtd-quickpicks">
            <span class="dtd-quickpicks-label">
              {{ 'settings.documentTemplate.fields.expirationPeriod' | translate }}:
            </span>
            <button
              mat-stroked-button
              type="button"
              class="dtd-quickpick"
              *ngFor="let period of expirationPeriodSuggestions"
              (click)="onPickExpirationPeriod(period, true)">
              {{ period }}
            </button>
            <button
              mat-stroked-button
              type="button"
              class="dtd-quickpick"
              (click)="onPickExpirationPeriod('', true)">
              {{ 'settings.documentTemplate.noExpirationPeriod' | translate }}
            </button>
          </div>

          <mat-spinner-button
            (btnClick)="onNewDocSubmit()"
            role="submit"
            [options]="newDocBtnOptions"
            class="btn-elevate">
          </mat-spinner-button>
        </div>
      </ng-container>

      <div class="dtd-empty dtd-empty-panel" *ngIf="docPanel === 'none' && docs.length">
        {{ 'settings.documentTemplate.empty.selectDoc' | translate }}
      </div>
    </div> <!-- /.col -->
  </div> <!-- /.row -->

  <!-- END - Level 3: documents -->

</mat-dialog-content>

<div class="row custom-dialog-footer">
  <div class="col-12 text-right">
    <a mat-raised-button color="primary" (click)="onClose()">
      <mat-icon fontSet="fal" fontIcon="fa-times-circle"></mat-icon>
      {{ 'common.cta.close' | translate }}
    </a>
  </div> <!-- /.col -->
</div> <!-- /.row -->
